import { useEffect, useRef, type JSX } from "react";

import { assignInlineVars } from "@vanilla-extract/dynamic";
import { motion, AnimatePresence } from "motion/react";

import { useElementSize } from "../../hooks";
import { rawPalette } from "../../styles/theme.css";
import { CollapseExpandTextButton } from "../CollapseExpandButton";
import { TRANSITION_DURATION } from "../CollapsibleHeight";

import * as theme from "./OverflowText.css";

interface OverflowTextProps {
    children: React.ReactNode;
    heightPx?: number;
    collapsed: boolean;
    onToggle: () => void;
    expandLabel: string;
    collapseLabel: string;
    // Any CSS color — the fade blends from it down to transparent.
    fadeColor?: string;
}
export function OverflowText({
    children,
    collapsed,
    onToggle,
    expandLabel = "Expand",
    collapseLabel = "Collapse",
    heightPx = 120,
    fadeColor = rawPalette.white,
}: OverflowTextProps): JSX.Element {
    const contentRef = useRef<HTMLDivElement>(null);
    const { height: contentHeight } = useElementSize(contentRef);
    const overflow = (contentHeight ?? 0) > heightPx;

    // The first render with a real measurement should snap to the resolved
    // state with no animation — only user-initiated toggles after that should
    // transition.
    const hasInitializedRef = useRef(false);
    useEffect(() => {
        if (contentHeight !== undefined) {
            hasInitializedRef.current = true;
        }
    }, [contentHeight]);
    const transition = {
        // eslint-disable-next-line react-hooks/refs
        duration: hasInitializedRef.current ? TRANSITION_DURATION : 0,
    };

    const handleExpand = () => {
        if (!collapsed) {
            return;
        }
        onToggle();
    };

    const animatedHeight = overflow && collapsed ? heightPx : "auto";

    const fadeStyle = assignInlineVars({
        [theme.fadeColorVar]: fadeColor,
    });

    return (
        <div className={theme.overflowText} style={fadeStyle}>
            <div className={theme.collapsibleText}>
                <AnimatePresence initial={false}>
                    {overflow && collapsed && (
                        <motion.div
                            initial="collapsed"
                            animate="open"
                            exit="collapsed"
                            transition={transition}
                            variants={{
                                open: { opacity: 1 },
                                collapsed: { opacity: 0 },
                            }}
                        >
                            <div
                                onClick={handleExpand}
                                className={theme.overflowTextOverlay}
                                tabIndex={0}
                                role="button"
                                aria-expanded={false}
                            />
                        </motion.div>
                    )}
                </AnimatePresence>
                <motion.div
                    initial={false}
                    animate={{ height: animatedHeight }}
                    transition={transition}
                >
                    <div ref={contentRef}>{children}</div>
                </motion.div>
            </div>
            {overflow && (
                <CollapseExpandTextButton
                    onClick={onToggle}
                    collapsed={collapsed}
                    expandLabel={expandLabel}
                    collapseLabel={collapseLabel}
                />
            )}
        </div>
    );
}
